// EmailVerificationStatus.js - Updated with improved resend functionality
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, CheckCircle, AlertCircle, RefreshCw, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';

const EmailVerificationStatus = () => {
  const { 
    user, 
    userProfile, 
    syncVerificationStatus, 
    resendVerificationEmail,
    loading 
  } = useAuth();
  
  const navigate = useNavigate();
  
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' | 'error' | 'info'
  const [isSyncing, setIsSyncing] = useState(false);

  // Check if user needs verification
  const needsVerification = user && (!user.emailVerified || (userProfile && !userProfile.is_verified));
  const hasFirebaseVerification = user?.emailVerified;
  const hasDatabaseVerification = userProfile?.is_verified;


// Auto-sync verification status every 2 minutes if not verified
  useEffect(() => {
    if (needsVerification && user && !loading) {
      const CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes instead of 30 seconds
      
      const interval = setInterval(async () => {
        console.log('🔄 Auto-checking verification status...');
        await handleSyncStatus();
      }, CHECK_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [needsVerification, user, loading]);

  const handleSyncStatus = async () => {
    if (!user) return;
    
    setIsSyncing(true);
    try {
      // Reload Firebase user to get latest verification status
      await user.reload();
      
      // Sync with backend if available
      if (syncVerificationStatus) {
        const updatedProfile = await syncVerificationStatus();
        
        if (user.emailVerified && updatedProfile?.is_verified) {
          setMessage('Email verified successfully! 🎉');
          setMessageType('success');
          
          // Clear message after 3 seconds then redirect
          setTimeout(() => {
            setMessage('');
            navigate('/dashboard');
          }, 3000);
        }
      } else {
        //console.log('Verification status synced, still not verified');
      }
    } catch (error) {
      console.error('Sync error:', error);
      setMessage('Error checking verification status.');
      setMessageType('error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    setMessage('');
    
    try {
      // Try using the AuthContext method first
      if (resendVerificationEmail) {
        await resendVerificationEmail();
      } else if (user) {
        // Fallback to Firebase's built-in method
        await user.sendEmailVerification();
      } else {
        throw new Error('No user found');
      }
      
      setMessage('Verification email sent! Please check your inbox and spam folder.');
      setMessageType('success');
      
      // Clear success message after 10 seconds
      setTimeout(() => setMessage(''), 10000);
    } catch (error) {
      console.error('Resend error:', error);
      setMessage('Failed to send verification email. Please try again.');
      setMessageType('error');
    } finally {
      setIsResending(false);
    }
  };

  // Don't show if no user or already verified
  if (!user || !needsVerification) {
    return null;
  }

  return (
    <div className="login-new-container">
      <div className="login-new-card">
        <div className="login-new-header">
          <div className="login-new-logo">
            <img src={logo} alt="Earn2Game Logo" />
          </div>
          <h1 className="login-new-title">
            Check Your Email! 📧
          </h1>
          <p className="login-new-subtitle">
            We've sent a verification email to <strong>{user.email}</strong>
          </p>
        </div>

        <div className="verification-status-container">
          <div className="verification-alert">
            <div className="verification-header">
              <AlertCircle className="alert-icon" size={24} />
              <h3>Email Verification Required</h3>
            </div>
            
            <div className="verification-content">
              <p>Please verify your email address to access all features.</p>
              
              {/* Verification Status Details */}
              <div className="verification-details">
                <div className={`status-item ${hasDatabaseVerification ? 'verified' : 'pending'}`}>
                  {hasDatabaseVerification ? (
                    <CheckCircle size={16} />
                  ) : (
                    <Clock size={16} />
                  )}
                  <span>Account Status: {hasDatabaseVerification ? 'Verified' : 'Pending'}</span>
                </div>
              </div>

              {/* Instructions */}
              <div className="verification-instructions">
                <h3>What's Next?</h3>
                <ol>
                  <li>Check your email inbox for a verification message</li>
                  <li>Click the verification link in the email</li>
                  <li>Return here and click "Check Verification Status"</li>
                </ol>
                
                <div className="verification-help">
                  <p><strong>Didn't receive the email?</strong></p>
                  <ul>
                    <li>Check your spam/junk folder</li>
                    <li>Make sure {user.email} is correct</li>
                    <li>Wait a few minutes - emails can be delayed</li>
                  </ul>
                </div>
              </div>

              {/* Messages */}
              {message && (
                <div className={`login-message ${messageType === 'error' ? 'login-error-message' : 'login-success-message'}`}>
                  {messageType === 'error' ? (
                    <AlertCircle size={20} />
                  ) : (
                    <CheckCircle size={20} />
                  )}
                  <span>{message}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="verification-actions">
                <button 
                  onClick={handleSyncStatus}
                  disabled={isSyncing}
                  className="btn btn-primary btn-large"
                >
                  {isSyncing ? (
                    <>
                      <RefreshCw className="spinning" size={16} />
                      Checking...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Check Verification Status
                    </>
                  )}
                </button>
                
                <button 
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="btn btn-secondary"
                >
                  {isResending ? (
                    <>
                      <RefreshCw className="spinning" size={16} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Resend Verification Email
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate('/login')}
                  className="btn btn-outline"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationStatus;